import { useRef, useState } from "react";
import { Camera, FolderOpen, SwitchCamera } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useCamera } from "@/hooks/useCamera";

const CameraStage = ({ attachVideo, ready, facing }) => (
  <div className="glass relative mt-6 aspect-[3/4] overflow-hidden">
    <video
      ref={attachVideo}
      autoPlay
      playsInline
      muted
      className={`h-full w-full object-cover ${facing === "user" ? "mirror" : ""} ${ready ? "opacity-100" : "absolute inset-0 opacity-0"}`}
      data-testid="camera-preview"
    />
    {!ready && (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center text-slate-400" data-testid="camera-unavailable">
        <Camera size={40} className="text-[var(--accent-soft)]" />
        <p className="text-sm">{ready === null ? "Kamera hazırlanıyor…" : "Kamera kullanılamıyor. Tarayıcıdan kamera iznini verin veya dosyadan fotoğraf seçin."}</p>
      </div>
    )}
    <div className="pointer-events-none absolute inset-4 rounded-2xl border border-white/20" />
  </div>
);

export const Capture = ({ onCapture }) => {
  const fileRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [facing, setFacing] = useState("user");
  const { attachVideo, ready, stop, captureBlob } = useCamera(started, facing);

  const snap = async () => {
    const blob = await captureBlob(facing === "user");
    if (!blob) return toast.error("Kamera henüz hazır değil, tekrar dene.");
    stop();
    onCapture(blob, URL.createObjectURL(blob));
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f || !f.type.startsWith("image/")) return f && toast.error("Lütfen bir fotoğraf seç.");
    stop();
    onCapture(f, URL.createObjectURL(f));
  };

  return (
    <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md" data-testid="capture-screen">
      <p className="eyebrow">Adım 1 / 2</p>
      <h1 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-4xl">Kadraja gir, gülümse.</h1>
      <p className="mt-2 text-sm text-slate-400">Fotoğrafın saklanmaz; sadece karikatürün üretilir.</p>

      {!started ? (
        <div className="mt-10 flex justify-center">
          <div className="rounded-full bg-[var(--accent-2)]/35 p-4 shadow-[0_0_60px_rgba(78,0,82,0.55)]">
            <button
              className="btn h-16 w-full min-w-[280px] rounded-full bg-[var(--accent-2)] px-10 font-display text-base font-extrabold uppercase tracking-wide text-white hover:brightness-125 sm:min-w-[340px]"
              onClick={() => setStarted(true)}
              data-testid="start-camera-button"
            >
              Başlamak için dokun
            </button>
          </div>
        </div>
      ) : (
        <>
          <CameraStage attachVideo={attachVideo} ready={ready} facing={facing} />
          <div className="mt-6 flex items-center justify-center gap-4">
            <button className="btn btn-ghost btn-icon" onClick={() => fileRef.current?.click()} aria-label="Dosyadan seç" title="Dosyadan seç" data-testid="upload-photo-button">
              <FolderOpen size={20} />
            </button>
            <button className="btn btn-accent h-20 w-20 rounded-full p-0" onClick={snap} disabled={!ready} aria-label="Fotoğraf çek" data-testid="camera-capture-button">
              <Camera size={30} />
            </button>
            <button className="btn btn-ghost btn-icon" onClick={() => setFacing((f) => (f === "user" ? "environment" : "user"))} disabled={!ready} aria-label="Kamerayı değiştir" data-testid="camera-flip-button">
              <SwitchCamera size={20} />
            </button>
          </div>
        </>
      )}
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="photo-file-input" />
    </motion.section>
  );
};
